import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { inr, wishlistUtils } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Stars from "@/components/Stars";

function stockBadge(stock) {
  if (stock === undefined || stock === null) return null;
  if (stock <= 0) return { label: "Out of Stock", tone: "bg-stone-800 text-white" };
  if (stock <= 3) return { label: `Only ${stock} left`, tone: "bg-[#8B5A2B] text-white" };
  return null;
}

export default function ProductCard({ p }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [saved, setSaved] = useState(wishlistUtils.has(p.id));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const update = () => setSaved(wishlistUtils.has(p.id));
    window.addEventListener("wishlist-updated", update);
    return () => window.removeEventListener("wishlist-updated", update);
  }, [p.id]);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Please log in to save to your wishlist.");
      nav("/login");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await wishlistUtils.remove(p.id);
        toast.success("Removed from wishlist");
      } else {
        await wishlistUtils.add(p.id);
        toast.success("Saved to wishlist");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update wishlist");
    } finally {
      setBusy(false);
    }
  };

  const badge = stockBadge(p.stock);

  return (
    <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="pcard block group relative">
      <div className="relative overflow-hidden bg-stone-100 aspect-[4/5]">
        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
        {badge && (
          <span data-testid={`product-stock-badge-${p.slug}`} className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase ${badge.tone}`}>
            {badge.label}
          </span>
        )}
        <button
          type="button"
          onClick={toggleWishlist}
          disabled={busy}
          data-testid={`product-wishlist-toggle-${p.slug}`}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${saved ? "bg-white text-[#B23A3A]" : "bg-white/85 text-stone-700 hover:bg-white hover:text-[#B23A3A]"} shadow-sm`}
        >
          <Heart size={16} fill={saved ? "#B23A3A" : "none"} strokeWidth={1.6} />
        </button>
      </div>
      <div className="mt-4 flex flex-col">
        <span className="text-[10px] tracking-[0.24em] uppercase text-[#8B5A2B]">{p.subcategory || p.category}</span>
        <h3 className="font-serif text-xl mt-1 text-stone-900 group-hover:text-[#8B5A2B] transition-colors">{p.name}</h3>
        {(p.rating_count || 0) > 0 && (
          <div className="mt-1 flex items-center gap-1.5" data-testid={`product-rating-${p.slug}`}>
            <Stars value={p.rating_avg || 0} size={11} />
            <span className="text-[11px] text-stone-500">({p.rating_count})</span>
          </div>
        )}
        <p data-testid={`product-price-${p.slug}`} className="mt-1 text-sm text-stone-600">{inr(p.price)}</p>
      </div>
    </Link>
  );
}
